/**
 * Onboarding Page
 * Entry point for new organization setup and subscription
 * 
 * Simple logic:
 * - If user has organizationId in URL or profile, use it
 * - Otherwise, show wizard to let user choose subscription type
 */

import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import OnboardingWizard from '../components/subscription/OnboardingWizard';
import SubscriptionLayout from '../components/subscription/SubscriptionLayout';
import { useAuth } from '../contexts/AuthContext';

const OnboardingPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const [organizationId, setOrganizationId] = useState<string | null>(null);
    const [organizationName, setOrganizationName] = useState<string>('');

    useEffect(() => {
        if (!user) {
            navigate('/admin/login');
            return;
        }

        // Get organizationId from URL or user profile
        const orgFromUrl = searchParams.get('org');
        const orgFromUser = user.organizationId;

        // Filter out invalid values
        const invalidOrgs = ['default-org', 'default-org-id', 'pending'];
        const orgId = (orgFromUrl && !invalidOrgs.includes(orgFromUrl))
            ? orgFromUrl
            : (orgFromUser && !invalidOrgs.includes(orgFromUser))
                ? orgFromUser
                : null;

        if (orgId) {
            setOrganizationId(orgId);
            // For individual subscriptions, orgId === userId
            if (orgId === user.id || orgId === 'individual') {
                setOrganizationName(user?.displayName || 'Personal Account');
            } else {
                setOrganizationName(user?.organizationName || '');
            }
        } else {
            // No organizationId - user needs to choose subscription type
            setOrganizationId('pending');
        }
    }, [searchParams, user, navigate]);

    const handleComplete = () => {
        if (organizationId && organizationId !== 'pending') {
            navigate(`/subscription?org=${organizationId}`);
        } else {
            navigate('/subscription');
        }
    };

    if (!user) {
        return null;
    }

    return (
        <SubscriptionLayout>
            <OnboardingWizard
                organizationId={organizationId || 'pending'}
                organizationName={organizationName}
                onComplete={handleComplete}
            />
        </SubscriptionLayout>
    );
};

export default OnboardingPage;
